import { ChatEntrySchema, type ChatEntry, type ChatResponse } from "@ark-bot/protocol";

import { emptyChat, seedChat, type ChatState } from "./state.js";

/**
 * The chats this page has shown, kept so that opening one again paints at
 * once instead of waiting on the host. The host still decides what is true:
 * its newest page is read on every open (ARCHITECTURE §5.5), and the kept
 * copy only stands until that page lands (`reconcile`).
 *
 * Two layers. In memory, the whole state of each chat, for moving between
 * chats. In the browser's storage, the newest page of each, for a page that
 * starts over: a reload, or a phone that put the app away and let it go.
 */

/** Where the browser keeps them. */
export const CHATS_KEY = "ark-bot.chats";
/** Raised when what is kept changes shape, so an older page's copy is dropped rather than misread. */
const VERSION = 1;
/** Chats held in memory, most recently shown kept. */
const MEMORY_CHATS = 24;
/** Chats kept in storage, and how many bubbles of each: one page, what a chat opens with. */
const STORED_CHATS = 16;
export const STORED_ENTRIES = 60;
/** Characters the kept chats may take together: storage holds about five million per site, shared with everything else here. */
const STORED_BUDGET = 1_500_000;
/** How long the bubbles have to be still before they are written down. */
const SAVE_AFTER_MS = 1_500;

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

interface StoredChat {
  /** When it was last shown, for deciding which to let go. */
  readonly at: number;
  readonly lastSeq: number;
  readonly hasMore: boolean;
  readonly messages: readonly ChatEntry[];
  readonly screens: ChatState["screens"];
  readonly context: ChatState["context"];
}

/**
 * Whose chats these are: the host and the account. A page that signs in as
 * someone else, or talks to another computer, never shows the last one's.
 */
export function cacheScope(host: string, accountId: string | null | undefined): string {
  return `${host}#${accountId ?? ""}`;
}

/** Only what reads as chat entries today; anything else is dropped, and the host's page fills in. */
function entriesOf(value: unknown): ChatEntry[] | null {
  if (!Array.isArray(value)) return null;
  const entries: ChatEntry[] = [];
  for (const item of value) {
    const parsed = ChatEntrySchema.safeParse(item);
    if (!parsed.success) return null;
    entries.push(parsed.data);
  }
  return entries;
}

function storedChatOf(value: unknown): StoredChat | null {
  if (typeof value !== "object" || value == null) return null;
  const record = value as Record<string, unknown>;
  const messages = entriesOf(record.messages);
  if (messages == null || typeof record.at !== "number" || typeof record.lastSeq !== "number") return null;
  return {
    at: record.at,
    lastSeq: record.lastSeq,
    hasMore: record.hasMore === true,
    messages,
    screens: Array.isArray(record.screens) ? (record.screens as ChatState["screens"]) : [],
    context: typeof record.context === "object" && record.context != null ? (record.context as ChatState["context"]) : null,
  };
}

function read(storage: Store | null, scope: string): Map<string, StoredChat> {
  const chats = new Map<string, StoredChat>();
  if (storage == null) return chats;
  try {
    const parsed = JSON.parse(storage.getItem(CHATS_KEY) ?? "null") as unknown;
    if (typeof parsed !== "object" || parsed == null) return chats;
    const record = parsed as Record<string, unknown>;
    if (record.v !== VERSION || record.scope !== scope || typeof record.chats !== "object" || record.chats == null) return chats;
    for (const [id, value] of Object.entries(record.chats)) {
      const chat = storedChatOf(value);
      if (chat != null) chats.set(id, chat);
    }
  } catch {
    // Storage refused, or it holds something that is not ours: start with nothing kept.
  }
  return chats;
}

/** The newest page of a chat, as it is kept. */
function freeze(state: ChatState, at: number): StoredChat {
  const trimmed = state.messages.length > STORED_ENTRIES;
  return {
    at,
    lastSeq: state.lastSeq,
    hasMore: state.hasMore || trimmed,
    messages: trimmed ? state.messages.slice(-STORED_ENTRIES) : state.messages,
    screens: state.screens,
    context: state.context,
  };
}

/** A kept page as a chat to show: what was said, and nothing in flight, since nobody can know that until the host says. */
function thaw(chat: StoredChat): ChatState {
  return { ...emptyChat, messages: [...chat.messages], lastSeq: chat.lastSeq, hasMore: chat.hasMore, screens: chat.screens, context: chat.context };
}

/**
 * The chat once the host's page has landed. When nothing has happened since
 * the kept copy was taken (the event cursor has not moved, and the newest
 * bubble is the same one), the copy is the truth, and keeps what the page
 * alone cannot give: older pages already read, and the outline's turns. Any
 * other way, the page starts the chat afresh, as it always did.
 */
export function reconcile(kept: ChatState | null, page: ChatResponse): ChatState {
  if (kept == null || kept.lastSeq !== page.eventSeq || kept.messages.at(-1)?.id !== page.entries.at(-1)?.id) return seedChat(page);
  return { ...kept, busy: page.busy, screens: page.screens ?? [], context: page.context ?? null };
}

export class ChatCache {
  private readonly memory = new Map<string, ChatState>();
  private readonly stored: Map<string, StoredChat>;
  private readonly shownAt = new Map<string, number>();
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly storage: Store | null,
    private readonly scope: string,
    private readonly now: () => number = Date.now,
  ) {
    this.stored = read(storage, scope);
  }

  /** The chat as last shown on this page, or as kept from an earlier one; null for one never seen. */
  get(agentId: string): ChatState | null {
    const held = this.memory.get(agentId);
    if (held != null) return held;
    const kept = this.stored.get(agentId);
    return kept == null ? null : thaw(kept);
  }

  /** The chat on screen, as it is now. Its bubbles are written down once they settle. */
  put(agentId: string, state: ChatState): void {
    const previous = this.memory.get(agentId);
    // Most recently shown last, so the first is the one to let go.
    this.memory.delete(agentId);
    this.memory.set(agentId, state);
    this.shownAt.set(agentId, this.now());
    while (this.memory.size > MEMORY_CHATS) {
      const [oldest, held] = this.memory.entries().next().value as [string, ChatState];
      // Let go of the whole state, but keep its page, as a reload would find it.
      this.stored.set(oldest, freeze(held, this.shownAt.get(oldest) ?? this.now()));
      this.memory.delete(oldest);
    }
    // Text streaming into the outline changes nothing that is kept; a bubble, or a page of them, does.
    if (previous?.messages !== state.messages || previous.hasMore !== state.hasMore) this.saveSoon();
  }

  /** A chat that is gone: deleted, or no longer in the roster. */
  forget(agentId: string): void {
    const held = this.memory.delete(agentId);
    const kept = this.stored.delete(agentId);
    this.shownAt.delete(agentId);
    if (held || kept) this.saveSoon();
  }

  /** Keeps only these chats: the roster as the host sent it whole. */
  keepOnly(agentIds: ReadonlySet<string>): void {
    for (const id of [...this.memory.keys(), ...this.stored.keys()]) if (!agentIds.has(id)) this.forget(id);
  }

  /**
   * Writes the kept chats down now: the page is being hidden and may not come
   * back. Newest first, as many as fit; a storage that refuses even one keeps
   * nothing, which only means the next visit waits on the host.
   */
  save(): void {
    if (this.timer != null) clearTimeout(this.timer);
    this.timer = null;
    if (this.storage == null) return;
    const chats = new Map(this.stored);
    for (const [id, state] of this.memory) chats.set(id, freeze(state, this.shownAt.get(id) ?? this.now()));
    let kept = [...chats].sort((a, b) => b[1].at - a[1].at).slice(0, STORED_CHATS);
    let text = this.serialise(kept);
    while (text.length > STORED_BUDGET && kept.length > 1) {
      kept = kept.slice(0, -1);
      text = this.serialise(kept);
    }
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        this.storage.setItem(CHATS_KEY, text);
        return;
      } catch {
        // Over the site's quota, with everything else it keeps: try with half as many.
        kept = kept.slice(0, Math.floor(kept.length / 2));
        text = this.serialise(kept);
      }
    }
    try {
      this.storage.removeItem(CHATS_KEY);
    } catch {
      // Storage is off altogether; nothing was kept, and nothing is lost.
    }
  }

  /** Stops the pending write; the page is done with this cache. */
  close(): void {
    if (this.timer != null) clearTimeout(this.timer);
    this.timer = null;
  }

  private saveSoon(): void {
    if (this.storage == null) return;
    if (this.timer != null) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.save(), SAVE_AFTER_MS);
  }

  private serialise(chats: ReadonlyArray<readonly [string, StoredChat]>): string {
    return JSON.stringify({ v: VERSION, scope: this.scope, chats: Object.fromEntries(chats) });
  }
}

/** Signing out: nothing said in this account stays on the device. */
export function forgetChats(storage: Store | null): void {
  try {
    storage?.removeItem(CHATS_KEY);
  } catch {
    // Storage is off: there was nothing kept to forget.
  }
}
